import React, { useEffect, useState } from "react";
import { useSOC } from "../context/SOCContext";
import { DataTable } from "../../common/DataTable";
import ApiLoader from "../../ui/ApiLoader";
import { REPORT_TYPES } from "../data/mockData";
import { downloadSOCReport } from "../../../services/axios/AuthApis/SOCApis";
import toast from "../../../utils/notify";

const Reports = () => {
  const { reports, fetchReports, generateReport, loading, errors } = useSOC();
  const [generateType, setGenerateType] = useState(REPORT_TYPES[0] || "");
  const [generating, setGenerating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchReports(); // guarded in context - won't refetch on tab revisit
  }, [fetchReports]);

  const handleGenerate = async () => {
    if (!generateType || generating) return;
    setGenerating(true);
    try {
      await generateReport(generateType);
    } catch (err) {
      // generateReport already toasts the error; nothing else to do here.
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (report) => {
    if (downloadingId) return;
    setDownloadingId(report.id);
    try {
      const blob = await downloadSOCReport(report.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${report.id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Unable to download report.");
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    {
      id: "id",
      accessorFn: (r) => r.id,
      header: "Report ID",
      enableSorting: false,
      size: 160,
      meta: { filter: { variant: "text", label: "Report ID", placeholder: "Search report ID" } },
      cell: ({ row }) => <span>{row.original.id}</span>,
    },
    {
      id: "type",
      accessorFn: (r) => r.type,
      header: "Type",
      enableSorting: false,
      size: 150,
      meta: {
        filter: {
          variant: "select",
          label: "Type",
          placeholder: "All report types",
          options: REPORT_TYPES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.type}</span>,
    },
    {
      id: "generatedAt",
      accessorFn: (r) => r.generatedAt,
      header: "Generated At",
      enableSorting: false,
      size: 180,
      cell: ({ row }) => <span>{row.original.generatedAt ? new Date(row.original.generatedAt).toLocaleString() : "-"}</span>,
    },
    {
      id: "generatedBy",
      accessorFn: (r) => r.generatedBy,
      header: "Generated By",
      enableSorting: false,
      size: 220,
      meta: { filter: { variant: "text", label: "Generated By", placeholder: "Search generated by" } },
      cell: ({ row }) => (
        <span title={row.original.generatedBy}>
          {row.original.generatedBy}
        </span>
      ),
    },
    {
      id: "summary",
      header: "Summary",
      enableSorting: false,
      size: 220,
      cell: ({ row }) => {
        const entries = Object.entries(row.original.summary || {});
        return (
          <div className="text-left text-xs text-gray-500">
            {entries.length === 0
              ? "-"
              : entries.map(([k, v]) => (
                  <div key={k} className="break-words">
                    {k}: {v}
                  </div>
                ))}
          </div>
        );
      },
    },
    {
      id: "download",
      header: "",
      enableSorting: false,
      size: 100,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => handleDownload(row.original)}
          disabled={downloadingId === row.original.id}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
        >
          {downloadingId === row.original.id ? "Downloading..." : "Download"}
        </button>
      ),
    },
  ];

  if (loading.reports && reports.length === 0) {
    return <ApiLoader variant="spinner" label="Loading Reports..." className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">SOC Reports</h3>
        <p className="text-xs text-gray-400 mb-4">
          Reports generated by the SOC backend. Use the Type column filter to narrow results.
        </p>

        {/* <div className="mt-4 flex items-center gap-3 border-t border-gray-100 pt-4">
          <select
            value={generateType}
            onChange={(e) => setGenerateType(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
          >
            {REPORT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {generating ? "Generating..." : "Generate Report"}
          </button>
        </div> */}
      </div>

      <DataTable
        columns={columns}
        data={reports}
        getRowId={(r) => String(r.id)}
        emptyMessage={errors.reports || "No reports found"}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        maxBodyHeight="65vh"
      />
    </div>
  );
};

export default Reports;
